import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, FormInstance, Select, Spin } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import { SelectProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useCounterpartServiceGetActiveContractListEndpointKey } from 'src/api/queries'
import {
  CounterpartService,
  GetActiveContractListResponse,
  GetActiveContractListResponseItem
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  activeDate?: string
  form: FormInstance
} & SelectProps

export const ContractSelect = ({ activeDate, form, ...props }: Props) => {
  const { t } = useLocales()
  const { userData } = useUser()

  const [selectedContractId, setSelectedContractId] = useState<
    string | undefined
  >()

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      String(userData?.currentLegalEntityId),
      useCounterpartServiceGetActiveContractListEndpointKey,
      activeDate
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await CounterpartService.getActiveContractListEndpoint(
        String(userData?.currentLegalEntityId),
        pageParam,
        DEFAULT_PER_PAGE,
        activeDate ? dayjs(activeDate).format(yearMonthDaySlash) : undefined
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetActiveContractListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = useMemo(() => {
    return data?.pages.flatMap(
      (page) =>
        page.data?.flatMap((item: GetActiveContractListResponseItem) =>
          item?.activeContracts
            ?.map((contract) =>
              contract
                ? {
                    value: contract.id,
                    label: contract.counterpart?.name
                  }
                : undefined
            )
            .filter(Boolean)
        ) || []
    ) as DefaultOptionType[]
  }, [data, t])

  useEffect(() => {
    if (options && options.length === 1) {
      const value = options[0].value
      setSelectedContractId(typeof value === 'string' ? value : undefined)
      form.setFieldValue('contractId', value)
    }
  }, [options])

  return (
    <Select
      {...props}
      allowClear
      options={options}
      popupMatchSelectWidth={true}
      value={selectedContractId}
      disabled={selectedContractId ? true : props.disabled}
      filterOption={false}
      loading={isFetching}
      placeholder={t('counterparts.contract_number')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        props.loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
    />
  )
}
